import { useState } from 'react';

/**
 * A written sum to LOOK at, with somewhere to carry.
 *
 * The full ColumnSum is an input: the child builds the answer a digit at a
 * time. This is the same layout without the answer cells, for when the answer
 * is still being picked from a list - because seeing 48 and 32 stacked, and
 * having a box to put the carried ten in, is worth having long before a child
 * is ready to type the answer.
 *
 * The carry boxes are scratch paper. Nothing reads them, nothing marks them,
 * and they are cleared with the question.
 */
export function StackedSum({
  top,
  bottom,
  operator,
  answerCells,
}: {
  top: number;
  bottom: number;
  operator: '+' | '−';
  answerCells: number;
}) {
  const [carries, setCarries] = useState<string[]>(() => Array(answerCells - 1).fill(''));

  const digitsOf = (n: number) => String(n).padStart(answerCells, ' ').split('');

  const bump = (index: number) => {
    setCarries((current) =>
      current.map((value, i) => (i === index ? (value === '' ? '1' : '') : value)),
    );
  };

  return (
    <div className="column-sum">
      <div className="column-sum__grid" style={{ ['--cells' as string]: answerCells }}>
        <div className="column-sum__gutter" />
        {carries.map((value, i) => (
          <button
            key={`carry-${i}`}
            type="button"
            className="column-sum__carry"
            aria-label={`Carry box, column ${carries.length - i}`}
            onClick={() => bump(i)}
          >
            {value}
          </button>
        ))}
        <div className="column-sum__spacer" />

        <div className="column-sum__gutter" />
        {digitsOf(top).map((d, i) => (
          <div key={`top-${i}`} className="column-sum__digit">
            {d.trim()}
          </div>
        ))}

        <div className="column-sum__gutter column-sum__op">{operator}</div>
        {digitsOf(bottom).map((d, i) => (
          <div key={`bottom-${i}`} className="column-sum__digit">
            {d.trim()}
          </div>
        ))}

        <div className="column-sum__rule" />
      </div>

      <p className="column-sum__note">
        Tap a dotted box if you carry a ten. It&rsquo;s just your working &mdash; nobody marks it.
      </p>
    </div>
  );
}
